import { motion, AnimatePresence } from 'framer-motion';
import { GoArrowDown } from 'react-icons/go';
import { FC, useEffect, useState } from 'react';

type ResourceFile = {
  url: string;
};

type Resource = {
  title?: string;
  file?: ResourceFile;
};

type SectionsResourcesProps = {
  data: {
    __typename: string;
    heading?: string;
    resources?: Resource[];
    padding: {
      mobile_top_padding: any;
      mobile_bottom_padding: any;
      desktop_top_padding: any;
      desktop_bottom_padding: any;
    };
  };
};
const ComponentSectionsResources: FC<SectionsResourcesProps> = ({ data }) => {
  const sectionsResources = data;
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
    exit: { opacity: 0, y: 10, transition: { duration: 0.3 } },
  };
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');

  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  return (
    <motion.section
      className="my-20 md:my-24 border-t border-[#9E703E66] pt-4 md:pt-5"
      id={sectionsResources.__typename}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
      style={{
        paddingTop: `${top}rem`,
        paddingBottom: `${bottom}rem`,
      }}
    >
      <div className="grid grid-cols-12 items-start">
        {/* Heading */}
        <div className="col-span-12 md:col-span-6">
          <h3 className="text-[22px] font-light leading-none mb-7 md:mb-4 text-[#9E703E]">
            {sectionsResources.heading}
          </h3>
        </div>

        {/* Resource Table */}
        <div className="col-span-12 md:col-span-6">
          <table className="w-full text-base font-normal leading-8">
            <tbody>
              <AnimatePresence>
                {sectionsResources.resources?.map((resource, index) => {
                  const hasFile = !!resource.file?.url;

                  return (
                    <motion.tr
                      key={index}
                      className="border-b border-[#9E703E66] text-[#9E703E] hover:border-[#9E703E] transition-all cursor-pointer"
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <td>{resource.title || 'Untitled'}</td>
                      <td className="text-right">
                        {hasFile ? (
                          <a
                            href={resource.file?.url}
                            className="flex items-center justify-end gap-1"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download <GoArrowDown />
                          </a>
                        ) : (
                          <span>No file available</span>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>
    </motion.section>
  );
};

export default ComponentSectionsResources;
